import { useState, useEffect, useCallback } from 'react'
import { OnboardingSession } from '../types/onboarding'

const SESSION_KEY = 'onboarding_session'

/**
 * Hook for managing onboarding session storage (FR-009, FR-045)
 * Persists data across page navigation before OAuth completion
 */
export function useOnboardingSession() {
  const [session, setSessionState] = useState<OnboardingSession>(() => {
    // Initialize from session storage on mount
    try {
      const stored = sessionStorage.getItem(SESSION_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to load onboarding session:', error)
    }

    // Default empty session
    return {
      mode: 'form-based',
      startedAt: Date.now(),
      lastUpdatedAt: Date.now(),
    }
  })

  // Save to session storage whenever session changes
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
    } catch (error) {
      console.error('Failed to save onboarding session:', error)
    }
  }, [session])

  // Update session data
  const updateSession = useCallback((updates: Partial<OnboardingSession>) => {
    setSessionState(prev => ({
      ...prev,
      ...updates,
      lastUpdatedAt: Date.now(),
    }))
  }, [])

  // Clear session (after successful onboarding)
  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY)
      setSessionState({
        mode: 'form-based',
        startedAt: Date.now(),
        lastUpdatedAt: Date.now(),
      })
    } catch (error) {
      console.error('Failed to clear onboarding session:', error)
    }
  }, [])

  // Check if session has data
  const hasData = Boolean(session.lovedOneName || session.lovedOnePhone)

  return {
    session,
    updateSession,
    clearSession,
    hasData,
  }
}
